import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider, useCompany } from "@/context/CompanyContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Landing from "@/pages/Landing";
import Interesados from "@/pages/Interesados";
import Team from "@/pages/Team";
import TeamCompany from "@/pages/TeamCompany";
import Calendario from "@/pages/Calendario";
import Billing from "@/pages/Billing";
import Audit from "@/pages/Audit";
import Connectivity from "@/pages/Connectivity";
import ErrorBoundary from "@/components/ErrorBoundary";
import PlatformPage from "@/pages/PlatformPage";
import EmailMarketing from "@/pages/EmailMarketing";
import EmailListDetail from "@/pages/EmailListDetail";
import EmailCampaignDetail from "@/pages/EmailCampaignDetail";
import MotorCreativo from "@/pages/MotorCreativo";
import GlobalCalendar from "@/pages/GlobalCalendar";
import CrossCompanyCalendar from "@/pages/CrossCompanyCalendar";
import Companies from "@/pages/Companies";
import ConsumoIA from "@/pages/ConsumoIA";
import Reportes from "@/pages/Reportes";
import Perfil from "@/pages/Perfil";
import Integraciones from "@/pages/Integraciones";
import Marketplace from "@/pages/Marketplace";
import MercadoLibrePage from "@/pages/MercadoLibrePage";
import Messenger from "@/pages/Messenger";
import ChatInterno from "@/pages/ChatInterno";
import Customers from "@/pages/Customers";
import BrandIdentities from "@/pages/BrandIdentities";
import Diagnostics from "@/pages/Diagnostics";
import CrossEmpresaDashboard from "@/pages/CrossEmpresaDashboard";
import YouTube from "@/pages/YouTube";
import LandingPublic from "@/pages/LandingPublic";
import LandingEditor from "@/pages/LandingEditor";
import ModulePlaceholder from "@/pages/ModulePlaceholder";
import { Sparkles, Calendar } from "lucide-react";

function ModuleGuard({ moduleKey, children }) {
  const { hasModule } = useAuth();
  if (!hasModule(moduleKey)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Fase 26 · Root gate: autenticado → dashboard, anónimo → LandingPublic
function RootGate() {
  const { user, status } = useAuth();
  if (status === "loading") return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPublic />;
}

const SUPER_MASTER_EMAIL = "fabiolicep@gmail.com";
function SuperMasterOnly({ children }) {
  const { user } = useAuth();
  if (!user || (user.email || "").toLowerCase() !== SUPER_MASTER_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/landing" element={<LandingPublic />} />
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/interesados"
              element={
                <ProtectedRoute masterOnly>
                  <Interesados />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipo"
              element={
                <ProtectedRoute masterOnly>
                  <Team />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipo-empresa"
              element={<TeamCompany />}
            />
            <Route
              path="/facturacion"
              element={<Billing />}
            />
            <Route
              path="/auditoria"
              element={
                <ProtectedRoute masterOnly>
                  <Audit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/empresas"
              element={
                <ProtectedRoute masterOnly>
                  <Companies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consumo-ia"
              element={
                <ProtectedRoute masterOnly>
                  <ConsumoIA />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reportes"
              element={<Reportes />}
            />
            <Route
              path="/integraciones"
              element={
                <ProtectedRoute masterOnly>
                  <Integraciones />
                </ProtectedRoute>
              }
            />
            <Route path="/marketplace" element={<ModuleGuard moduleKey="marketplace"><Marketplace /></ModuleGuard>} />
            <Route path="/mercadolibre" element={<ModuleGuard moduleKey="mercadolibre"><MercadoLibrePage /></ModuleGuard>} />
            <Route path="/messenger" element={<ModuleGuard moduleKey="messenger"><Messenger /></ModuleGuard>} />
            <Route path="/meta-pages" element={<ModuleGuard moduleKey="meta_pages"><PlatformPage platformKey="facebook" /></ModuleGuard>} />
            <Route path="/chat" element={<ModuleGuard moduleKey="chat_interno"><ChatInterno /></ModuleGuard>} />
            <Route
              path="/perfil"
              element={<Perfil />}
            />
            <Route
              path="/conectividad"
              element={<ErrorBoundary section="Centro de Conectividad"><Connectivity /></ErrorBoundary>}
            />
            <Route
              path="/clientes"
              element={
                <ModuleGuard moduleKey="clientes">
                  <ErrorBoundary section="Clientes"><Customers /></ErrorBoundary>
                </ModuleGuard>
              }
            />
            <Route
              path="/motor-creativo/identidades"
              element={
                <ModuleGuard moduleKey="motor_creativo">
                  <ErrorBoundary section="Identidades de marca"><BrandIdentities /></ErrorBoundary>
                </ModuleGuard>
              }
            />
            <Route
              path="/diagnostico"
              element={<ErrorBoundary section="Diagnóstico"><Diagnostics /></ErrorBoundary>}
            />
            <Route
              path="/configuracion/landing"
              element={
                <SuperMasterOnly>
                  <ErrorBoundary section="Editor de Landing"><LandingEditor /></ErrorBoundary>
                </SuperMasterOnly>
              }
            />
            <Route
              path="/dashboard-cross"
              element={<ErrorBoundary section="Dashboard cross-empresa"><CrossEmpresaDashboard /></ErrorBoundary>}
            />

            {/* Módulos de redes sociales — Fase 2 */}
            <Route
              path="/instagram"
              element={
                <ModuleGuard moduleKey="instagram">
                  <PlatformPage platformKey="instagram" />
                </ModuleGuard>
              }
            />
            <Route
              path="/facebook"
              element={
                <ModuleGuard moduleKey="facebook">
                  <PlatformPage platformKey="facebook" />
                </ModuleGuard>
              }
            />
            <Route
              path="/tiktok"
              element={
                <ModuleGuard moduleKey="tiktok">
                  <PlatformPage platformKey="tiktok" />
                </ModuleGuard>
              }
            />
            <Route
              path="/whatsapp"
              element={
                <ModuleGuard moduleKey="whatsapp">
                  <PlatformPage platformKey="whatsapp" />
                </ModuleGuard>
              }
            />
            <Route
              path="/youtube"
              element={
                <ModuleGuard moduleKey="youtube">
                  <ErrorBoundary section="YouTube"><YouTube /></ErrorBoundary>
                </ModuleGuard>
              }
            />

            {/* Placeholders para Fases 3-5 */}
            <Route
              path="/email-marketing"
              element={
                <ModuleGuard moduleKey="email_marketing">
                  <EmailMarketing />
                </ModuleGuard>
              }
            />
            <Route
              path="/email-marketing/listas/:listId"
              element={
                <ModuleGuard moduleKey="email_marketing">
                  <EmailListDetail />
                </ModuleGuard>
              }
            />
            <Route
              path="/email-marketing/campanas/:campaignId"
              element={
                <ModuleGuard moduleKey="email_marketing">
                  <EmailCampaignDetail />
                </ModuleGuard>
              }
            />
            <Route
              path="/motor-creativo"
              element={
                <ModuleGuard moduleKey="motor_creativo">
                  <MotorCreativo />
                </ModuleGuard>
              }
            />
            <Route
              path="/calendario"
              element={<Calendario />}
            />
            <Route
              path="/calendario-social"
              element={<GlobalCalendar />}
            />
            <Route
              path="/calendario-multi"
              element={<CrossCompanyCalendar />}
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;
